import React, { Component } from "react";
import styled from "styled-components";

import { ActionPermissionsObject } from "common/interfaces";
import { getStaticUrl } from "js/config";
import PresentationPermissionsController from "js/controllers/PresentationPermissionsController";
import WorkspaceController, { getSlidesInPresentationsCount } from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { normalizePermissionForDisplay } from "js/core/utilities/permissions";
import { trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import { ShowDialogAsync, ShowErrorDialog, ShowSnackBar } from "js/react/components/Dialogs/BaseDialog";
import { Gap10, Gap30 } from "js/react/components/Gap";
import { ConfirmInviteDialog } from "js/react/components/InviteInput/ConfirmInviteDialog";
import { createInviteSuggestionContext } from "js/react/components/InviteInput/InviteContext";
import { InviteTagsInput } from "js/react/components/InviteInput/InviteTagsInput";
import { FlexBox } from "js/react/components/LayoutGrid";
import { BlueSwitch } from "js/react/components/Switch";
import TextArea from "js/react/components/TextArea";
import {
    BlueButton,
    Section,
    UIPane,
    UIPaneContents,
    UIPaneHeader
} from "js/react/components/UiComponents";

import PermissionsList from "./PermissionsList";
import { t } from "js/i18n";

const logger = getLogger(LogGroup.COLLABORATION);

// Anchor id for the comment-notification settings section, used by the comments panel
// shortcut to scroll this section into view
export const COMMENT_NOTIFICATIONS_SECTION_ID = "comment-notifications";

const InviteActions = styled.div`
    text-align: right;
`;

const SlackIcon = styled.img`
    width: 24px;
    height: 24px;
`;

class CollaboratePane extends Component {
    constructor(props) {
        super(props);

        this.state = {
            inviteContext: null,
            customMessage: "",
            isNotificationsSettingsFetching: true,
            notificationsSettings: {
                notifyOnEdit: props.presentation.get("userId") === app.user.id,
                notifyOnAssignedSlideChange: true,
                notifyOnComment: true,
                notifyOnCommentMentioningMe: true,
            },
        };

        this.notifySectionRef = React.createRef();
    }

    componentDidMount() {
        const { presentation } = this.props;
        const presentationNotificationsSettings = app.user.get("presentationNotificationsSettings");

        let { notificationsSettings } = this.state;
        if (presentationNotificationsSettings && presentationNotificationsSettings[presentation.id]) {
            notificationsSettings = { ...notificationsSettings, ...presentationNotificationsSettings[presentation.id] };
        }

        const inviteContext = createInviteSuggestionContext({
            includeFolders: true,
            presentationId: presentation.id,
            prohibitExternalWorkspaceCollaboration: !WorkspaceController.actionPermissions[ActionPermissionsObject.EXTERNAL_WORKSPACES_COLLABORATION].use,
            saveContext: inviteContext => this.setState({ inviteContext }),
        });

        this.setState({
            inviteContext,
            notificationsSettings,
            isNotificationsSettingsFetching: false
        });

        // When opened via the comments panel shortcut, scroll the notification settings
        // into view (the collaborator list above can be long enough to push it off-screen)
        if (window.location.hash === `#${COMMENT_NOTIFICATIONS_SECTION_ID}`) {
            setTimeout(() => {
                this.notifySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 300);
        }
    }

    componentWillUnmount() {
        this.state.inviteContext?.destroy();
    }

    async handleNotificationsSettingsChange(changedKey, changedValue) {
        const { presentation } = this.props;
        const { notificationsSettings } = this.state;

        const presentationId = presentation.id;
        const oldNotificationsSettings = notificationsSettings;

        const updatedNotificationsSettings = { ...notificationsSettings, [changedKey]: changedValue };

        try {
            app.user.update({ presentationNotificationsSettings: { [presentationId]: updatedNotificationsSettings } });
            await app.user.updatePromise;
            this.setState({ notificationsSettings: updatedNotificationsSettings });
            const currentSlideCount = await getSlidesInPresentationsCount(presentation.getWorkspaceId()).catch(() => 0);
            const props = {
                current_slide_count: currentSlideCount,
                setting_changed: changedKey,
                old_value: oldNotificationsSettings[changedKey],
                new_value: changedValue,
                scope: "presentation",
                presentation_id: presentationId,
                workspace_id: presentation.getWorkspaceId()
            };
            trackActivity("Notification", "SettingChanged", null, null, props, { audit: true });
        } catch (err) {
            logger.error(err, "[CollaboratePane] handleNotificationsSettingsChange() error updating user's notifications settings");
            this.setState({ notificationsSettings: oldNotificationsSettings });
        }
    }

    handleSendInvites = async () => {
        const { presentation } = this.props;
        const { inviteContext } = this.state;

        const presentationId = presentation.id;

        try {
            const users = inviteContext.getSelectedUniqueUsers();
            const selection = inviteContext.getSelectionWithRedundanciesRemoved();

            await ShowDialogAsync(ConfirmInviteDialog, {
                hasPermissionEditing: true,
                users,
                callback: permission => {
                    withProgressDialog({
                        title: t("Sending invites..."),
                        action: async ({ setProgress }) => {
                            try {
                                const emails = [];
                                const folders = [];

                                // This is old logic and will be refactored together with ivite context refactor
                                const displayPerm = normalizePermissionForDisplay(permission);
                                selection.forEach(item => {
                                    switch (item.type) {
                                        case "folder":
                                            if (item.permissionType !== displayPerm) {
                                                folders.push(item.id);
                                            }
                                            break;
                                        case "user":
                                        default:
                                            emails.push(item.email);
                                            break;
                                    }
                                });

                                const totalActions = (emails.length ? 1 : 0) + folders.length;

                                const write = permission !== "read";
                                if (emails.length > 0) {
                                    await PresentationPermissionsController.addPresentationPermissionsForUsers({
                                        users: emails.map(email => ({ email, permissions: { write: { object: write, permissions: write }, read: { object: true, permissions: true }, owner: false } })),
                                        analyticsProps: {
                                            location: "settings"
                                        }
                                    });
                                    if (totalActions > 1) {
                                        setProgress(1 / totalActions);
                                    }
                                }
                                if (folders.length > 0) {
                                    for (const folderId of folders) {
                                        await PresentationPermissionsController.addPresentationPermissionsForFolder({
                                            folderId,
                                            presentationPermissions: { write: { object: write, permissions: write }, read: { object: true, permissions: true } },
                                            analyticsProps: {
                                                location: "settings"
                                            }
                                        });
                                        if (totalActions > 1) {
                                            setProgress((2 + folders.indexOf(folderId)) / totalActions);
                                        }
                                    }
                                }

                                let message = emails[0];
                                if (emails.length > 1) {
                                    message += ` and ${emails.length - 1} other${emails.length > 2 ? "s" : ""}`;
                                }
                                ShowSnackBar({ message: t("Email sent to {{recipients}}", { recipients: message }) });

                                await inviteContext.init();
                            } catch (err) {
                                logger.error(err, "CollaboratePane handleSendInvites() failed", { presentationId });

                                ShowErrorDialog({
                                    error: t("Oops!"),
                                    message: err.message
                                });
                            }
                        }
                    });
                },
            });
        } catch (err) {
            logger.error(err, "CollaboratePane handleSendInvites() failed", { presentationId });
            ShowErrorDialog({
                error: t("Oops!"),
                message: err.message
            });
        }
    }

    handleChangeCustomMessage = customMessage => {
        this.setState({
            customMessage
        });
    }

    render() {
        const {
            inviteContext,
            customMessage,
            isNotificationsSettingsFetching,
            notificationsSettings: {
                notifyOnEdit,
                notifyOnAssignedSlideChange,
                notifyOnComment,
                notifyOnCommentMentioningMe,
            },
        } = this.state;
        const { presentation } = this.props;

        return (
            <UIPane className="collaborate-pane">
                <UIPaneHeader>
                    {WorkspaceController.isMultiUserWorkspace ? t("Collaborate with your team") : t("Collaborate")}
                </UIPaneHeader>
                <UIPaneContents>
                    {
                        inviteContext &&
                        <Section title={t("Invite Collaborators")}>
                            <InviteTagsInput
                                inviteContext={inviteContext}
                                sourceName="CollaboratePane"
                            />

                            <Gap10 />
                            <TextArea
                                isVisible
                                text={customMessage}
                                onChange={customMessage => this.handleChangeCustomMessage(customMessage)}
                                placeholder={t("Add a note...")}
                            />

                            <Gap10 />
                            <InviteActions>
                                <BlueButton
                                    disabled={!inviteContext.selection.length}
                                    onClick={this.handleSendInvites}
                                >
                                    {t("Send Invite")}
                                </BlueButton>
                            </InviteActions>
                        </Section>
                    }

                    <Section title={t("Who has access")}>
                        <PermissionsList presentationId={presentation.id} canChangeOwnership/>
                    </Section>

                    <div ref={this.notifySectionRef} id={COMMENT_NOTIFICATIONS_SECTION_ID}>
                        <Section title={t("Notify me when") + ":"}>
                            <BlueSwitch
                                checked={notifyOnEdit}
                                onChange={() => this.handleNotificationsSettingsChange("notifyOnEdit", !notifyOnEdit)}
                                disabled={isNotificationsSettingsFetching}
                                label={t("Presentation is changed")}
                            />
                            <BlueSwitch
                                checked={notifyOnAssignedSlideChange}
                                onChange={() => this.handleNotificationsSettingsChange("notifyOnAssignedSlideChange", !notifyOnAssignedSlideChange)}
                                disabled={isNotificationsSettingsFetching}
                                label={t("Slides I own are changed")}
                            />
                            <BlueSwitch
                                checked={notifyOnComment}
                                onChange={() => this.handleNotificationsSettingsChange("notifyOnComment", !notifyOnComment)}
                                disabled={isNotificationsSettingsFetching}
                                label={t("Comments are added")}
                            />
                            <BlueSwitch
                                checked={notifyOnCommentMentioningMe}
                                onChange={() => this.handleNotificationsSettingsChange("notifyOnCommentMentioningMe", !notifyOnCommentMentioningMe)}
                                disabled={isNotificationsSettingsFetching}
                                label={t("Comments that involve me are added")}
                            />

                            <Gap30 />
                            <FlexBox left middle gap="15px">
                                <SlackIcon src={getStaticUrl("/images/slack.svg")} />
                                <span>
                                    {t("Get notifications right away.")} <a href="https://www.beautiful.ai/integrations/slack" target="_blank" rel="noopener noreferrer">{t("Add Beautiful.ai to Slack")}</a>
                                </span>
                            </FlexBox>
                        </Section>
                    </div>
                </UIPaneContents>
            </UIPane>
        );
    }
}

export default PresentationPermissionsController.withStateInitializer(CollaboratePane, ({ presentation }) => presentation?.id);
